import React, { use, useEffect, useMemo, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import ErrorMessage from '@components-clean/core/error';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import AddCriticaltoQualityFactorDialog, {
    CtQfFormInputs,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/add-critical-to-quality-factor-dialog';
import AssignFunctionalDataReviewToCtQfDialog, {
    ILibraryFunctionalDataReviewFieldsSubset,
} from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/assign-fdrs-to-ctqf-dialog';
// eslint-disable-next-line max-len
import EditCriticaltoQualityFactorDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/edit-critical-to-quality-factor-dialog';
import AssignCtqToRiskDialog from '@pages-clean/risk-assessment/risk-log/dialogs/assign-ctq-dialog';
import UnassignCtQDialog from '@pages-clean/risk-assessment/risk-log/dialogs/unassign-ctq-dialog';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType, NamedItemType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import type { getNamedItemsByType } from '@queries/generated/common.generated';
import type {
    StudyFields,
    CriticalVariableFields,
    CriticalVariableListItemFields,
} from '@queries/generated/fragments.generated';
import {
    updateCriticalVariable,
    updateCriticalVariableVariables,
    assignLibraryFunctionalDataReview,
    assignLibraryFunctionalDataReviewVariables,
    getCriticalVariablesForRisk_OpraQueries_criticalVariablesForRisk_CriticalVariable as CtqItem,
    getCriticalVariablesForRisk,
    getCriticalVariablesForRiskVariables,
    assignCriticalVariablesToRisk,
    assignCriticalVariablesToRiskVariables,
    createCriticalVariableAndAssignRisk,
    createCriticalVariableAndAssignRiskVariables,
    getUnassignedCriticalVariablesForRisk,
    getUnassignedCriticalVariablesForRiskVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    ADD_CRITICAL_TO_QUALITY_FACTOR_AND_ASSIGN_RISK,
    ASSIGN_CRITICAL_VARIABLES,
    ASSIGN_LIBRARY_FUNCTIONAL_DATA_REVIEWS,
    GET_CRITICAL_VARIABLES_FOR_RISK,
    GET_UNASSIGNED_CRITICAL_VARIABLES_FOR_RISK,
    UPDATE_CRITICAL_TO_QUALITY_FACTOR,
    GET_COMPLETE_RISKS_RISK_LOG,
} from '@queries/risk-assessment';

type FlattenCtq = {
    id: string;
    recordId?: number | null;
    description?: string | null;
    justification?: string | null;
    sourceSystem?: string | null;
    dataSource?: string | null;
    active?: boolean | null;
    isCritical?: boolean | null;
    cTQType?: INamedItem | null;
    cTQEDCEntry?: INamedItem | null;
    vendor?: INamedItem | null;
    createdBy?: string | null;
    updatedBy?: string | null;
    created?: string | null;
    updated?: string | null;
    riskCount: number | null;
};

function isCtq(x: CtqItem | null): x is CtqItem {
    return x !== null;
}

const uniq = (vals: (string | null | undefined)[]) =>
    Array.from(new Set(vals.filter((v): v is string => !!v && v.trim().length > 0)));

const mapCtqFactorForDialogs = (raw: CtqItem): CtqItem => {
    const createdByValue = raw.createdBy;
    const updatedByValue = raw.updatedBy;

    const createdBy =
        createdByValue && typeof createdByValue === 'object'
            ? `${createdByValue.firstName ?? ''} ${createdByValue.lastName ?? ''}`.trim()
            : (createdByValue ?? '');

    const updatedBy =
        updatedByValue && typeof updatedByValue === 'object'
            ? `${updatedByValue.firstName ?? ''} ${updatedByValue.lastName ?? ''}`.trim()
            : (updatedByValue ?? '');

    return {
        ...(raw as unknown as Record<string, unknown>),
        createdBy,
        updatedBy,
    } as unknown as CtqItem;
};

const CtQsArea = () => {
    const { t: tGrid } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.grid' });
    const { t: tCommon } = useTranslation('common');
    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const { current } = use(RiskLogContext);

    const studyId = current?.study?.id as string | undefined;
    const studyNumber = current?.study?.studyNumber ?? '';
    const selectedCount = current?.selectionState?.selectedItemsCount ?? 0;
    const riskId = current?.selectionState?.selectedItem?.id ?? null;
    const riskStatement = current?.selectionState?.selectedItem?.statement ?? '';

    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const multiSelected = selectedCount > 1;

    const [loadCtqs, { data, loading, error, called, refetch }] = useLazyQuery<
        getCriticalVariablesForRisk,
        getCriticalVariablesForRiskVariables
    >(GET_CRITICAL_VARIABLES_FOR_RISK, { nextFetchPolicy: 'cache-first' });

    const [loadUnassignedCtqs, { data: unassignedData, loading: loadingUnassigned }] = useLazyQuery<
        getUnassignedCriticalVariablesForRisk,
        getUnassignedCriticalVariablesForRiskVariables
    >(GET_UNASSIGNED_CRITICAL_VARIABLES_FOR_RISK, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });

    const [updateCriticalToQualityFactor] = useMutation<updateCriticalVariable, updateCriticalVariableVariables>(
        UPDATE_CRITICAL_TO_QUALITY_FACTOR,
    );

    const [executeAssignCriticalVariablesToRisk, { loading: assigningCtqs }] = useMutation<
        assignCriticalVariablesToRisk,
        assignCriticalVariablesToRiskVariables
    >(ASSIGN_CRITICAL_VARIABLES);

    const [executeCreateCriticalVariableAndAssignRisk, { loading: creatingCtqAndAssign }] = useMutation<
        createCriticalVariableAndAssignRisk,
        createCriticalVariableAndAssignRiskVariables
    >(ADD_CRITICAL_TO_QUALITY_FACTOR_AND_ASSIGN_RISK);

    const [assignLibraryFunctionalDataReviews, { loading: assigningFdrs }] = useMutation<
        assignLibraryFunctionalDataReview,
        assignLibraryFunctionalDataReviewVariables
    >(ASSIGN_LIBRARY_FUNCTIONAL_DATA_REVIEWS);

    const { data: ctqTypeData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQ_TYPE },
    });
    const { data: edcEntryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQEDC_ENTRY },
    });
    const { data: vendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.VENDOR },
    });

    useEffect(() => {
        if (studyId && riskId && !multiSelected) {
            Promise.resolve(loadCtqs({ variables: { studyId, riskId } })).catch(() => {});
        }
    }, [studyId, riskId, multiSelected, loadCtqs]);

    const rawCtqs: CtqItem[] = useMemo(() => (data?.criticalVariablesForRisk ?? []).filter(isCtq), [data]);

    const rawById = useMemo(() => {
        const map: Record<string, CtqItem> = {};
        rawCtqs.forEach((cv) => {
            map[cv.id] = cv;
        });
        return map;
    }, [rawCtqs]);

    const ctqTypeLov = useMemo(() => {
        const fromServer = ctqTypeData?.namedItemsByType?.map((n) => n?.name as string) ?? [];
        const fromGrid = rawCtqs.map((cv) => cv.cTQType?.name).filter(Boolean) as string[];
        return uniq([...fromServer, ...fromGrid]);
    }, [ctqTypeData, rawCtqs]);

    const edcEntryLov = useMemo(() => {
        const fromServer = edcEntryData?.namedItemsByType?.map((n) => n?.name as string) ?? [];
        const fromGrid = rawCtqs.map((cv) => cv.cTQEDCEntry?.name).filter(Boolean) as string[];
        return uniq([...fromServer, ...fromGrid]);
    }, [edcEntryData, rawCtqs]);

    const flattened: FlattenCtq[] = useMemo(() => {
        const mapped = rawCtqs.map((cv) => ({
            id: cv.id,
            recordId: cv.recordId ?? null,
            description: cv.description ?? '',
            justification: cv.justification ?? '',
            sourceSystem: cv.sourceSystem ?? '',
            dataSource: cv.dataSource ?? '',
            active: cv.active ?? false,
            isCritical: cv.isCritical ?? false,
            cTQType: cv.cTQType
                ? { id: cv.cTQType.id, name: cv.cTQType.name ?? '', active: cv.cTQType.active ?? true }
                : null,
            cTQEDCEntry: cv.cTQEDCEntry
                ? { id: cv.cTQEDCEntry.id, name: cv.cTQEDCEntry.name ?? '', active: cv.cTQEDCEntry.active ?? true }
                : null,
            vendorList: cv.vendors?.map((a) => a?.name).join(', '),
            vendors: cv.vendors,
            createdBy: `${cv.createdBy?.firstName ?? ''} ${cv.createdBy?.lastName ?? ''}`.trim(),
            updatedBy: `${cv.updatedBy?.firstName ?? ''} ${cv.updatedBy?.lastName ?? ''}`.trim(),
            created: cv.created ?? null,
            updated: cv.updated ?? null,
            riskCount: cv.riskCount ?? null,
        }));
        return mapped.sort((a, b) => {
            if (a.active !== b.active) return a.active ? -1 : 1;
            if (a.isCritical !== b.isCritical) return a.isCritical ? -1 : 1;
            const da = a.updated ? new Date(a.updated).getTime() : 0;
            const db = b.updated ? new Date(b.updated).getTime() : 0;
            return db - da;
        });
    }, [rawCtqs]);

    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const [showEditDialog, setShowEditDialog] = useState(false);
    const [ctqFactor, setCtqFactor] = useState<CtqItem | undefined>(undefined);
    const showUnassignCtQfDialog = useBoolean(false);
    const [showAssignCtqDialog, setShowAssignCtqDialog] = useState(false);
    const [showAddCtqDialog, setShowAddCtqDialog] = useState(false);

    const [showAssignFdrDialog, setShowAssignFdrDialog] = useState(false);
    const [ctqForFdrDialog, setCtqForFdrDialog] = useState<CriticalVariableFields | undefined>(undefined);

    const unassignedCtqs: CriticalVariableListItemFields[] = useMemo(
        () =>
            (unassignedData?.unassignedCriticalVariablesForRisk ?? []).filter(
                (x): x is CriticalVariableListItemFields => x !== null,
            ),
        [unassignedData],
    );

    if (!studyId || !riskId) return <div />;

    if (error) return <ErrorMessage />;

    const exportFilename = `${(riskStatement ?? '').substring(0, 80)}-CtQs_${studyNumber ?? ''}_${stringDateWithoutTimeFromCurrentUtcDate(
        'DD-MMM-YYYY',
    )}`;

    const onSubmitEdit = async (updateData: CtQfFormInputs) => {
        try {
            const response = await updateCriticalToQualityFactor({
                variables: {
                    criticalVariable: {
                        studyId,
                        active: updateData.isActive,
                        description: updateData.description,
                        justification: updateData.justification,
                        typeId: updateData.type?.id,
                        edcentryId: updateData.edcEntry?.id,
                        vendorIds: updateData.vendors?.map((v) => v.id) ?? [],
                        sourceSystem: updateData.system,
                        dataSource: updateData.dataSource,
                        isCritical: updateData.isCritical,
                        id: ctqFactor?.id,
                    },
                },
            });

            if (!response?.data?.updateCriticalVariable) {
                ShowError(tCommon('errorMessage'));
                return;
            }

            setShowEditDialog(false);
            await refetch?.();
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const onSubmitUnAssign = (result: string) => {
        if (result === 'success') ShowSuccess(tCommon('actionsSuccessfulToast'));
        else ShowError(tCommon('errorMessage'));
        showUnassignCtQfDialog.setFalse();
    };

    const openAssignOrAddDialog = async () => {
        if (!studyId || !riskId) return;
        try {
            const response = await loadUnassignedCtqs({ variables: { studyId, riskId } });

            const list =
                response.data?.unassignedCriticalVariablesForRisk?.filter(
                    (x): x is CriticalVariableListItemFields => x !== null,
                ) ?? [];

            if (list.length > 0) {
                setShowAssignCtqDialog(true);
            } else {
                setShowAddCtqDialog(true);
            }
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const handleAssignCtqs = async (selectedCtqIds: string[], close: boolean) => {
        if (!riskId || !studyId || !selectedCtqIds.length) return;
        const alreadyAssignedIds = rawCtqs.map((cv) => cv.id);
        const mergedIds = Array.from(new Set([...alreadyAssignedIds, ...selectedCtqIds]));

        try {
            const response = await executeAssignCriticalVariablesToRisk({
                variables: {
                    id: riskId,
                    criticalVariableIds: mergedIds,
                },
                refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } }],
            });

            if (!response?.data?.assignCriticalVariables) {
                ShowError(tCommon('errorMessage'));
                return;
            }
            await refetch();

            setFilterState({
                logic: 'and',
                filters: [],
            });

            if (!close) {
                await loadUnassignedCtqs({ variables: { studyId, riskId } });
            }

            if (close) {
                setShowAssignCtqDialog(false);
            }

            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const handleAssignCtqNew = () => {
        setShowAssignCtqDialog(false);
        setShowAddCtqDialog(true);
    };

    const buildCriticalVariableInput = (formData: CtQfFormInputs) => ({
        studyId,
        active: formData.isActive,
        description: formData.description,
        justification: formData.justification,
        typeId: formData.type?.id,
        edcentryId: formData.edcEntry?.id,
        vendorIds: formData.vendors?.map((v) => v.id) ?? [],
        sourceSystem: formData.system,
        dataSource: formData.dataSource,
        isCritical: formData.isCritical,
    });

    const createCtqAndAssignToRisk = async (formData: CtQfFormInputs): Promise<CriticalVariableFields | null> => {
        if (!studyId || !riskId) return null;
        if (creatingCtqAndAssign) return null;

        const criticalVariableInput = buildCriticalVariableInput(formData);

        try {
            const response = await executeCreateCriticalVariableAndAssignRisk({
                variables: {
                    criticalVariable: criticalVariableInput,
                    riskId,
                },
                refetchQueries: [{ query: GET_COMPLETE_RISKS_RISK_LOG, variables: { studyId } }],
            });

            const created = response?.data?.createCriticalVariableAndAssignRisk;
            if (!created?.id) {
                ShowError(tCommon('errorMessage'));
                return null;
            }

            await refetch();

            setFilterState({
                logic: 'and',
                filters: [],
            });

            ShowSuccess(tCommon('actionsSuccessfulToast'));

            return created as unknown as CriticalVariableFields;
        } catch {
            ShowError(tCommon('errorMessage'));
            return null;
        }
    };

    const handleAddCtqSubmit = async (formData: CtQfFormInputs) => {
        const created = await createCtqAndAssignToRisk(formData);
        if (!created) return;

        setShowAddCtqDialog(false);
        setShowAssignCtqDialog(false);
    };

    const handleAddCtqAndAssignReviewsSubmit = async (formData: CtQfFormInputs) => {
        const created = await createCtqAndAssignToRisk(formData);
        if (!created) return;

        setShowAddCtqDialog(false);
        setShowAssignCtqDialog(false);
        setCtqForFdrDialog(created);
        setShowAssignFdrDialog(true);
    };

    const handleAssignFunctionalDataReviewsSubmit = async (
        close: boolean,
        libraryFunctionalDataReviews: ILibraryFunctionalDataReviewFieldsSubset[],
    ) => {
        if (!ctqForFdrDialog?.id) return false;

        try {
            const response = await assignLibraryFunctionalDataReviews({
                variables: {
                    id: ctqForFdrDialog.id,
                    libraryFdrs: libraryFunctionalDataReviews,
                },
            });

            const updatedCtq = response?.data?.assignLibraryFunctionalDataReview as
                CriticalVariableFields | null | undefined;

            if (!updatedCtq) {
                ShowError(tCommon('errorMessage'));
                return false;
            }

            if (close) {
                setShowAssignFdrDialog(false);
                setCtqForFdrDialog(undefined);
            } else {
                setCtqForFdrDialog(updatedCtq);
            }

            await refetch?.();

            ShowSuccess(tCommon('actionsSuccessfulToast'));
            return true;
        } catch {
            ShowError(tCommon('errorMessage'));
            return false;
        }
    };

    const handleAddClick = async () => {
        if (!riskId) return;
        await openAssignOrAddDialog();
    };

    const columns: GridColumnProps[] = [
        {
            id: 'description',
            field: 'description',
            title: tGrid('columns.description'),
            lockedByDefault: true,
            width: 1,
            minWidth: 300,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'active',
            field: 'active',
            title: tGrid('columns.active'),
            cells: { data: YesNoCell },
            width: 1,
            minWidth: 110,
            filter: 'boolean',
        },
        {
            id: 'cTQType',
            field: 'cTQType',
            title: tGrid('columns.type'),
            width: 1,
            minWidth: 150,
            filter: 'text',
            filterKey: 'name',
            lovData: ctqTypeLov,
            lovFilterContains: true,
            cells: {
                data: ActiveInactiveCell({
                    inactive: (n: INamedItem) => n?.active === false,
                    renderValue: (n: INamedItem) => n?.name,
                }),
            },
        },
        {
            id: 'justification',
            field: 'justification',
            title: tGrid('columns.justification'),
            width: 1,
            minWidth: 250,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'sourceSystem',
            field: 'sourceSystem',
            title: tGrid('columns.sourceSystem'),
            width: 1,
            minWidth: 180,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'dataSource',
            field: 'dataSource',
            title: tGrid('columns.dataSource'),
            width: 1,
            minWidth: 180,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'cTQEDCEntry',
            field: 'cTQEDCEntry',
            title: tGrid('columns.edcEntry'),
            width: 1,
            minWidth: 150,
            filter: 'text',
            filterKey: 'name',
            lovData: edcEntryLov,
            lovFilterContains: true,
            cells: {
                data: ActiveInactiveCell({
                    inactive: (n: INamedItem) => n?.active === false,
                    renderValue: (n: INamedItem) => n?.name,
                }),
            },
        },
        {
            id: 'vendors',
            field: 'vendorList',
            title: tGrid('columns.vendors'),
            width: 1,
            minWidth: 200,
            filter: 'text',
            lovData: vendorData?.namedItemsByType
                ? vendorData.namedItemsByType.map((item) => item?.name as string)
                : [],
            lovFilterContains: true,
            cells: {
                data: TooltipCell({
                    isCsv: true,
                }),
            },
        },
        {
            id: 'isCritical',
            field: 'isCritical',
            title: tGrid('columns.isCritical'),
            cells: { data: YesNoCell },
            width: 1,
            minWidth: 110,
            filter: 'boolean',
        },
        {
            id: 'createdBy',
            field: 'createdBy',
            title: tGrid('columns.createdBy'),
            width: 1,
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'created',
            field: 'created',
            title: tGrid('columns.created'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
            showByDefault: false,
        },
        { id: 'updatedBy', field: 'updatedBy', title: tGrid('columns.updatedBy'), width: 1, minWidth: 150 },
        {
            id: 'updated',
            field: 'updated',
            title: tGrid('columns.updated'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 130,
            cellName: 'DateCell',
        },
        {
            width: 1,
            minWidth: 200,
            id: 'recordId',
            field: 'recordId',
            showByDefault: false,
            title: tGrid('columns.id'),
        },
    ];

    return (
        <div style={{ height: '100%' }} className="remove-card-padding">
            <Grid
                excludeHiddenRowMenuActions
                key={riskId ?? 'no-risk'}
                title={tGrid('title')}
                maxHeight="100%"
                testDataId="ram-risklog-ctqs-grid"
                data={flattened}
                columns={columns}
                loading={loading || !called}
                useLoadingOverlay
                showAddIcon
                addCallback={handleAddClick}
                addRecordTooltip="Assign CtQ(s)"
                scrollable="virtual"
                dataItemKey="id"
                rowMenuActionsWidth={58}
                filterable
                filter={filterState}
                canEdit={canEdit}
                removeCardBorder
                rowMenuActions={[
                    {
                        id: 1,
                        label: tGrid('actions.edit'),
                        isEdit: true,
                        onClick: (dataItem: unknown) => {
                            const item = dataItem as FlattenCtq;
                            const raw = rawById[item.id];
                            if (raw) {
                                const mapped = mapCtqFactorForDialogs(raw);
                                setCtqFactor(mapped);
                                setShowEditDialog(true);
                            }
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'] as IconProp,
                    },
                    {
                        id: 2,
                        label: tGrid('actions.unassignCtq'),
                        onClick: (dataItem: unknown) => {
                            const item = dataItem as FlattenCtq;
                            const raw = rawById[item.id];
                            if (raw) {
                                const mapped = mapCtqFactorForDialogs(raw);
                                setCtqFactor(mapped);
                                showUnassignCtQfDialog.setTrue();
                            }
                        },
                        icon: ['fal', 'link-slash'] as IconProp,
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={exportFilename}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['risk-log-ctqs'])}
            />

            {showEditDialog && ctqFactor && (
                <EditCriticaltoQualityFactorDialog
                    onSubmit={onSubmitEdit}
                    onClose={() => setShowEditDialog(false)}
                    ctqFactor={ctqFactor as unknown as CriticalVariableFields}
                />
            )}

            {showUnassignCtQfDialog.value && ctqFactor && current?.selectionState?.selectedItem && (
                <UnassignCtQDialog
                    onSubmit={onSubmitUnAssign}
                    onClose={() => showUnassignCtQfDialog.setFalse()}
                    ctq={ctqFactor as unknown as CriticalVariableFields}
                    ctqs={flattened}
                    risk={current?.selectionState.selectedItem}
                />
            )}

            {showAssignCtqDialog && (
                <AssignCtqToRiskDialog
                    riskStatement={riskStatement}
                    assignableCtqs={unassignedCtqs}
                    assigning={assigningCtqs || loadingUnassigned}
                    onClose={() => {
                        setShowAssignCtqDialog(false);
                    }}
                    onSubmit={handleAssignCtqs}
                    onAddCtqOpen={handleAssignCtqNew}
                />
            )}

            {showAddCtqDialog && (
                <AddCriticaltoQualityFactorDialog
                    onSubmit={handleAddCtqSubmit}
                    onClose={() => {
                        setShowAddCtqDialog(false);
                    }}
                    onAddFunctionalReviewsSubmit={handleAddCtqAndAssignReviewsSubmit}
                />
            )}
            {showAssignFdrDialog && current?.study && ctqForFdrDialog && (
                <AssignFunctionalDataReviewToCtQfDialog
                    study={current.study as StudyFields}
                    onClose={() => {
                        setShowAssignFdrDialog(false);
                        setCtqForFdrDialog(undefined);
                    }}
                    onSubmit={handleAssignFunctionalDataReviewsSubmit}
                    associatedCriticalVariable={ctqForFdrDialog}
                    updatingFunctionalDataReviews={assigningFdrs}
                />
            )}
        </div>
    );
};

export default CtQsArea;
